import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  Search, 
  Filter, 
  RefreshCw, 
  User,
  Calendar,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  X,
  Check,
  Ban
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminTimeEntries() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [staffFilter, setStaffFilter] = useState('all');
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [adjustedDuration, setAdjustedDuration] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState({});
  const [staffList, setStaffList] = useState([]);

  useEffect(() => {
    fetchEntries();
  }, [statusFilter, staffFilter]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      let url = `${API}/api/admin/time-entries`;
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (staffFilter !== 'all') params.append('staff_username', staffFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
        setSummary(data.summary || {});
        
        // Extract unique staff members
        const uniqueStaff = [...new Set(data.entries.map(e => e.staff_username))];
        setStaffList(uniqueStaff);
      }
    } catch (err) {
      console.error('Error fetching time entries:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (entryId, status) => {
    setSubmitting(true);
    try {
      const body = {
        status: status,
        admin_notes: adminNotes || null,
        adjusted_duration: adjustedDuration ? parseInt(adjustedDuration) : null
      };

      const res = await fetch(`${API}/api/admin/time-entries/${entryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        setShowModal(false);
        setSelectedEntry(null);
        setAdminNotes('');
        setAdjustedDuration('');
        fetchEntries();
      }
    } catch (err) {
      console.error('Error updating time entry:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedEntries.length === 0) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/admin/time-entries/bulk-approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(selectedEntries)
      });
      
      if (res.ok) {
        setSelectedEntries([]);
        fetchEntries();
      }
    } catch (err) {
      console.error('Error bulk approving entries:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleSelectEntry = (entryId) => {
    setSelectedEntries(prev => 
      prev.includes(entryId) 
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const selectAllPending = () => {
    const pendingIds = entries
      .filter(e => e.status === 'pending')
      .map(e => e.entry_id);
    setSelectedEntries(pendingIds);
  };

  const openEntry = (entry) => {
    setSelectedEntry(entry);
    setAdminNotes('');
    setAdjustedDuration(entry.duration_minutes?.toString() || '');
    setShowModal(true);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0m';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs === 0) return `${mins}m`;
    return `${hrs}h ${mins}m`;
  };

  const statusColors = {
    pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertCircle },
    approved: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
    denied: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle }
  };

  const filteredEntries = entries.filter(entry => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      entry.client_name?.toLowerCase().includes(query) ||
      entry.staff_name?.toLowerCase().includes(query) ||
      entry.task_type?.toLowerCase().includes(query) ||
      entry.description?.toLowerCase().includes(query)
    );
  });

  const pendingCount = entries.filter(e => e.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Clock className="w-7 h-7 text-blue-500" />
            Time Entries
          </h1>
          <p className="text-slate-500 mt-1">Review and approve staff time entries</p>
        </div>
        <div className="flex gap-2">
          {selectedEntries.length > 0 && (
            <button
              onClick={handleBulkApprove}
              disabled={submitting}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-medium flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              Approve Selected ({selectedEntries.length})
            </button>
          )}
          <button
            onClick={fetchEntries}
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Total Entries</p>
          <p className="text-2xl font-bold text-slate-800">{summary.total_entries || 0}</p>
        </div>
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
          <p className="text-sm text-amber-600">Pending Approval</p>
          <p className="text-2xl font-bold text-amber-700">{summary.pending_approval || 0}</p>
        </div>
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
          <p className="text-sm text-blue-600">Total Hours</p>
          <p className="text-2xl font-bold text-blue-700">{summary.total_hours || 0}h</p>
        </div>
        <div className="bg-green-50 rounded-xl border border-green-200 p-4">
          <p className="text-sm text-green-600">Billable Hours</p>
          <p className="text-2xl font-bold text-green-700">{summary.billable_hours || 0}h</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search entries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm"
          />
        </div>
        <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
          </select>
          <select
            value={staffFilter}
            onChange={(e) => setStaffFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="all">All Staff</option>
            {staffList.map(staff => (
              <option key={staff} value={staff}>{staff}</option>
            ))}
          </select>
          {pendingCount > 0 && (
            <button
              onClick={selectAllPending}
              className="px-3 py-2 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-200"
            >
              Select All Pending
            </button>
          )}
        </div>
      </div>

      {/* Entries Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No time entries found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    <input
                      type="checkbox"
                      checked={selectedEntries.length === filteredEntries.filter(e => e.status === 'pending').length && pendingCount > 0}
                      onChange={selectAllPending}
                      className="rounded border-slate-300"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Staff</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Task</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Duration</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Billable</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredEntries.map((entry) => {
                  const StatusIcon = statusColors[entry.status]?.icon || AlertCircle;
                  return (
                    <motion.tr
                      key={entry.entry_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-slate-50"
                    >
                      <td className="px-4 py-3">
                        {entry.status === 'pending' && (
                          <input
                            type="checkbox"
                            checked={selectedEntries.includes(entry.entry_id)}
                            onChange={() => toggleSelectEntry(entry.entry_id)}
                            className="rounded border-slate-300"
                          />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-medium text-sm">
                            {entry.staff_name?.charAt(0) || 'S'}
                          </div>
                          <span className="text-sm text-slate-800">{entry.staff_name || entry.staff_username}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{entry.client_name}</td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-800 capitalize">{entry.task_type?.replace('_', ' ')}</span>
                        {entry.description && (
                          <p className="text-xs text-slate-400 truncate max-w-[150px]">{entry.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{formatDate(entry.start_time)}</td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-800">{formatDuration(entry.duration_minutes)}</span>
                        {entry.adjusted_duration && entry.adjusted_duration !== entry.duration_minutes && (
                          <span className="text-xs text-amber-600 block">
                            Adjusted: {formatDuration(entry.adjusted_duration)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {entry.is_billable ? (
                          <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                            <DollarSign className="w-3 h-3" />
                            Yes
                          </span>
                        ) : (
                          <span className="text-slate-400 text-sm">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium capitalize ${statusColors[entry.status]?.bg} ${statusColors[entry.status]?.text}`}>
                          <StatusIcon className="w-3 h-3" />
                          {entry.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {entry.status === 'pending' ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleAction(entry.entry_id, 'approved')}
                              className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200"
                              title="Approve"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openEntry(entry)}
                              className="p-1.5 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200"
                              title="Review"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleAction(entry.entry_id, 'denied')}
                              className="p-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                              title="Deny"
                            >
                              <Ban className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => openEntry(entry)}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            View
                          </button>
                        )}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Entry Detail Modal */}
      <AnimatePresence>
        {showModal && selectedEntry && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-lg overflow-hidden"
            >
              {/* Modal Header */}
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">Time Entry Details</h2>
                    <p className="text-sm text-slate-500 mt-1">
                      {selectedEntry.staff_name} • {formatDate(selectedEntry.start_time)}
                    </p>
                  </div>
                  <button 
                    onClick={() => setShowModal(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg text-slate-400"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500 uppercase mb-1">Client</p>
                    <p className="font-medium text-slate-800">{selectedEntry.client_name}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500 uppercase mb-1">Task Type</p>
                    <p className="font-medium text-slate-800 capitalize">{selectedEntry.task_type?.replace('_', ' ')}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500 uppercase mb-1">Duration</p>
                    <p className="font-medium text-slate-800">{formatDuration(selectedEntry.duration_minutes)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500 uppercase mb-1">Billable</p>
                    <p className="font-medium text-slate-800">{selectedEntry.is_billable ? 'Yes' : 'No'}</p>
                  </div>
                </div>

                {selectedEntry.description && (
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500 uppercase mb-1">Description</p>
                    <p className="text-sm text-slate-700">{selectedEntry.description}</p>
                  </div>
                )}

                {selectedEntry.status === 'pending' && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">
                        Adjust Duration (optional)
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={adjustedDuration}
                          onChange={(e) => setAdjustedDuration(e.target.value)}
                          placeholder={selectedEntry.duration_minutes?.toString()}
                          className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        />
                        <span className="text-sm text-slate-500">minutes</span>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">
                        Admin Notes (optional)
                      </label>
                      <textarea
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        placeholder="Add notes for the staff member..."
                        rows={2}
                        className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm"
                      />
                    </div>
                  </>
                )}

                {selectedEntry.admin_notes && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-xs text-amber-600 uppercase mb-1">Admin Notes</p>
                    <p className="text-sm text-amber-800">{selectedEntry.admin_notes}</p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              {selectedEntry.status === 'pending' && (
                <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3">
                  <button
                    onClick={() => handleAction(selectedEntry.entry_id, 'denied')}
                    disabled={submitting}
                    className="px-4 py-2 bg-white border border-red-200 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 flex items-center gap-2"
                  >
                    <Ban className="w-4 h-4" />
                    Deny
                  </button>
                  <button
                    onClick={() => handleAction(selectedEntry.entry_id, 'approved')}
                    disabled={submitting}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                  >
                    {submitting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    Approve
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
